import { motion } from 'framer-motion';

const EditFields = ({ editFormData, handleEditInputChange, stacked }) => {
  const inputClass = `input input-bordered ${stacked ? 'input-md' : 'input-sm'} w-full`;
  const fields = [
    { name: 'sessionDate', label: 'Date', type: 'date' },
    { name: 'sessionName', label: 'Session', type: 'text', placeholder: 'Enter session name' },
    { name: 'playerName', label: 'Player', type: 'text', placeholder: 'Enter player name' },
    { name: 'buyIn', label: 'Buy-in ($)', type: 'number', numeric: true },
    { name: 'cashOut', label: 'Cash-out ($)', type: 'number', numeric: true },
  ];

  return fields.map(field => {
    const input = (
      <input
        id={stacked ? `edit-${field.name}` : undefined}
        type={field.type}
        name={field.name}
        value={editFormData[field.name] ?? ''}
        onChange={handleEditInputChange}
        className={`${inputClass} ${field.numeric ? 'text-right' : ''}`}
        placeholder={field.placeholder}
        inputMode={field.numeric ? 'decimal' : undefined}
        step={field.numeric ? '1' : undefined}
        min={field.numeric ? '0' : undefined}
      />
    );
    if (!stacked) {
      return <td key={field.name} className={field.numeric ? 'text-right' : ''}>{input}</td>;
    }
    return (
      <div key={field.name} className={`flex flex-col gap-1 ${field.name === 'sessionDate' || field.name === 'sessionName' ? 'col-span-2' : ''}`}>
        <label htmlFor={`edit-${field.name}`} className="text-xs font-medium opacity-70">{field.label}</label>
        {input}
      </div>
    );
  });
};

const SessionHistoryTable = ({
  performanceData,
  isGlobalEditMode,
  selectedSessions,
  editingRow,
  editFormData,
  formatDate,
  formatMoney,
  handleSessionSelect,
  handleEditInputChange,
  handleEditClick,
  handleCancelEdit,
  handleSaveEdit,
  calculateTotalBuyIn,
  calculateTotalCashOut,
  calculateTotalProfit,
  setSelectedSessions
}) => {
  const allSelected = performanceData.length > 0 && selectedSessions.length === performanceData.length;
  const toggleAll = (checked) => {
    setSelectedSessions(checked ? performanceData.map(item => item._id) : []);
  };
  const profitClass = (value) => (value >= 0 ? 'text-success' : 'text-error');

  return (
    <>
      {/* Mobile: stacked cards so the edit form and its buttons always fit */}
      <div className="md:hidden flex flex-col gap-3">
        {isGlobalEditMode && (
          <label className="flex items-center gap-3 px-1 text-sm">
            <input
              type="checkbox"
              className="checkbox checkbox-sm"
              checked={allSelected}
              onChange={(e) => toggleAll(e.target.checked)}
            />
            Select all
          </label>
        )}

        {performanceData.map(session => {
          const isEditing = editingRow === session._id;
          return (
            <div
              key={session._id}
              className={`rounded-xl border border-base-200 p-4 ${isEditing ? 'bg-base-200/70' : 'bg-base-100'}`}
            >
              {isEditing ? (
                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-2 gap-3">
                    <EditFields editFormData={editFormData} handleEditInputChange={handleEditInputChange} stacked />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <button className="btn btn-ghost" onClick={handleCancelEdit}>Cancel</button>
                    <button className="btn btn-primary" onClick={() => handleSaveEdit(session._id)}>Save</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  {isGlobalEditMode && (
                    <input
                      type="checkbox"
                      className="checkbox checkbox-sm mt-1"
                      checked={selectedSessions.includes(session._id)}
                      onChange={() => handleSessionSelect(session._id)}
                      aria-label={`Select ${session.sessionName || 'session'}`}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-3">
                      <p className="font-medium truncate">{session.sessionName || 'Unnamed Game'}</p>
                      <p className={`font-semibold tabular-nums ${profitClass(session.profit)}`}>${formatMoney(session.profit)}</p>
                    </div>
                    <p className="text-xs opacity-70 mt-1">
                      {formatDate(session.sessionDate)} · {session.playerName}
                    </p>
                    <p className="text-xs opacity-70 mt-1 tabular-nums">
                      In ${formatMoney(session.buyIn)} / Out ${formatMoney(session.cashOut)}
                    </p>
                  </div>
                  {isGlobalEditMode && !editingRow && (
                    <button className="btn btn-ghost btn-sm" onClick={() => handleEditClick(session)}>
                      Edit
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}

        <div className="flex items-center justify-between rounded-xl bg-base-200/50 px-4 py-3 font-semibold">
          <span>Total</span>
          <span className={`tabular-nums ${profitClass(calculateTotalProfit())}`}>${formatMoney(calculateTotalProfit())}</span>
        </div>
      </div>

      {/* Desktop: table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="table w-full">
          <thead>
            <tr className="bg-base-200/50">
              {isGlobalEditMode && (
                <th className="w-16">
                  <label>
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={allSelected}
                      onChange={(e) => toggleAll(e.target.checked)}
                    />
                  </label>
                </th>
              )}
              <th>Date</th>
              <th>Session</th>
              <th>Player</th>
              <th className="text-right">Buy-in</th>
              <th className="text-right">Cash-out</th>
              <th className="text-right">Profit/Loss</th>
              {isGlobalEditMode && <th className="w-24">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {performanceData.map(session => {
              const isEditing = editingRow === session._id;
              return (
                <motion.tr
                  key={session._id}
                  className={`hover:bg-base-200/50 transition-colors ${isEditing ? 'bg-base-200/70' : ''}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {isGlobalEditMode && (
                    <td>
                      <label>
                        <input
                          type="checkbox"
                          className="checkbox"
                          checked={selectedSessions.includes(session._id)}
                          onChange={() => handleSessionSelect(session._id)}
                        />
                      </label>
                    </td>
                  )}
                  {isEditing ? (
                    <EditFields editFormData={editFormData} handleEditInputChange={handleEditInputChange} />
                  ) : (
                    <>
                      <td className="font-medium">{formatDate(session.sessionDate)}</td>
                      <td>{session.sessionName || 'Unnamed Game'}</td>
                      <td>{session.playerName}</td>
                      <td className="text-right">${formatMoney(session.buyIn)}</td>
                      <td className="text-right">${formatMoney(session.cashOut)}</td>
                    </>
                  )}
                  <td className={`text-right font-semibold ${profitClass(session.profit)}`}>
                    ${formatMoney(session.profit)}
                  </td>
                  {isGlobalEditMode && (
                    <td>
                      <div className="flex gap-2 justify-end">
                        {isEditing ? (
                          <>
                            <button className="btn btn-ghost btn-xs" onClick={handleCancelEdit}>Cancel</button>
                            <button className="btn btn-primary btn-xs" onClick={() => handleSaveEdit(session._id)}>Save</button>
                          </>
                        ) : !editingRow && (
                          <button className="btn btn-ghost btn-xs" onClick={() => handleEditClick(session)}>Edit</button>
                        )}
                      </div>
                    </td>
                  )}
                </motion.tr>
              );
            })}
          </tbody>
          <tfoot className="border-t-2 border-base-200">
            <tr className="font-bold">
              {isGlobalEditMode && <td></td>}
              <td colSpan={3}>Totals</td>
              <td className="text-right">${formatMoney(calculateTotalBuyIn())}</td>
              <td className="text-right">${formatMoney(calculateTotalCashOut())}</td>
              <td className={`text-right ${profitClass(calculateTotalProfit())}`}>
                ${formatMoney(calculateTotalProfit())}
              </td>
              {isGlobalEditMode && <td></td>}
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
};

export default SessionHistoryTable;
